import { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import {
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Typography,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Switch,
    FormControlLabel,
} from '@mui/material';
import { Delete, Download, CloudUpload } from '@mui/icons-material';
import unsplash from '../api/unsplash';

const AddAllegatiInput = ({ onFilesChange, edizioneId, isEditMode }) => {
    const [newFiles, setNewFiles] = useState([]);
    const [existingFiles, setExistingFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [sharedFileNames, setSharedFileNames] = useState(new Set());
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        fileId: null,
        filename: '',
    });

    useEffect(() => {
        const loadExistingFiles = async () => {
            try {
                setLoading(true);
                const response = await unsplash.get(
                    `getMaterialeEdizione/${edizioneId}`,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('jwt')}`,
                        },
                    }
                );
                setExistingFiles(response.data.files || []);
            } catch (err) {
                console.error('Errore nel caricamento file esistenti:', err);
                setErrorMsg('Errore nel caricamento dei file esistenti');
            } finally {
                setLoading(false);
            }
        };

        if (edizioneId && isEditMode) {
            loadExistingFiles();
        }
    }, [edizioneId, isEditMode]);

    const { getRootProps, getInputProps } = useDropzone({
        multiple: true,
        maxSize: 10 * 1024 * 1024,
        onDrop: (acceptedFiles, rejectedFiles) => {
            if (rejectedFiles.length > 0) {
                setErrorMsg(
                    'Alcuni file superano i 10 MB e non sono stati aggiunti.'
                );
            } else {
                setErrorMsg('');
            }
            const updated = [...newFiles, ...acceptedFiles];
            setNewFiles(updated);
            onFilesChange?.(updated, Array.from(sharedFileNames));
        },
    });

    const removeNewFile = (name) => {
        const updated = newFiles.filter((file) => file.name !== name);
        const nextShared = new Set(sharedFileNames);
        nextShared.delete(name);
        setNewFiles(updated);
        setSharedFileNames(nextShared);
        onFilesChange?.(updated, Array.from(nextShared));
        setErrorMsg('');
    };

    const toggleNewFileSharing = (fileName, checked) => {
        const nextShared = new Set(sharedFileNames);
        if (checked) nextShared.add(fileName);
        else nextShared.delete(fileName);
        setSharedFileNames(nextShared);
        onFilesChange?.(newFiles, Array.from(nextShared));
    };

    const toggleExistingFileSharing = async (file, checked) => {
        try {
            await unsplash.put(
                `materialeEdizione/${edizioneId}/${file.id}/condivisione`,
                { condiviso: checked },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('jwt')}`,
                    },
                }
            );
            setExistingFiles((current) =>
                current.map((item) =>
                    item.id === file.id
                        ? { ...item, condiviso_partecipanti: checked ? 1 : 0 }
                        : item
                )
            );
        } catch (err) {
            console.error('Errore aggiornamento condivisione:', err);
            setErrorMsg('Impossibile aggiornare la visibilità del file');
        }
    };

    const openDeleteDialog = (fileId, filename) => {
        setDeleteDialog({
            open: true,
            fileId,
            filename,
        });
    };

    const closeDeleteDialog = () => {
        setDeleteDialog({
            open: false,
            fileId: null,
            filename: '',
        });
    };

    const handleDeleteExistingFile = async () => {
        const { fileId } = deleteDialog;

        try {
            await unsplash.delete(
                `eliminaSingoloFileEdizione/${edizioneId}/${fileId}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('jwt')}`,
                    },
                }
            );

            setExistingFiles((prev) =>
                prev.filter((file) => file.id !== fileId)
            );

            closeDeleteDialog();
        } catch (err) {
            console.error("Errore nell'eliminazione del file:", err);
            setErrorMsg("Errore nell'eliminazione del file");
            closeDeleteDialog();
        }
    };

    const handleDownload = async (file) => {
        try {
            const response = await unsplash.get(
                `materialeEdizione/${edizioneId}/${file.id}/download`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('jwt')}`,
                    },
                    responseType: 'blob',
                }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = file.filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Errore download materiale:', err);
            setErrorMsg('Download del file non disponibile');
        }
    };

    return (
        <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
                Materiale del Corso
            </Typography>

            {loading && (
                <div style={{ textAlign: 'center', margin: '1rem 0' }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Caricamento file...</Typography>
                </div>
            )}

            <div
                {...getRootProps()}
                style={{
                    border: '2px dashed #1976d2',
                    padding: '1.5rem',
                    textAlign: 'center',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    backgroundColor: '#f9f9f9',
                    marginBottom: '1rem',
                }}
            >
                <input {...getInputProps()} />
                <CloudUpload
                    style={{
                        fontSize: 48,
                        color: '#1976d2',
                        marginBottom: '0.5rem',
                    }}
                />
                <p>Trascina i file qui o clicca per selezionare</p>
                <Typography variant="caption" color="textSecondary">
                    Dimensione massima: 10MB per file
                </Typography>
            </div>

            {errorMsg && (
                <Typography color="error" variant="body2" gutterBottom>
                    {errorMsg}
                </Typography>
            )}

            {newFiles.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Nuovi file da caricare:
                    </Typography>
                    {newFiles.map((file, idx) => (
                        <div
                            key={idx}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                padding: '0.5rem 1rem',
                                marginBottom: '0.5rem',
                                background: '#fff',
                            }}
                        >
                            <span>{file.name}</span>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={sharedFileNames.has(file.name)}
                                        onChange={(event) =>
                                            toggleNewFileSharing(
                                                file.name,
                                                event.target.checked
                                            )
                                        }
                                    />
                                }
                                label="Mostra ai partecipanti"
                            />
                            <span
                                onClick={() => removeNewFile(file.name)}
                                style={{
                                    color: 'red',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                }}
                            >
                                ×
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {isEditMode && existingFiles.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                    <Typography variant="subtitle1" gutterBottom>
                        File già caricati:
                    </Typography>
                    <List dense>
                        {existingFiles.map((file) => (
                            <ListItem key={file.id} divider>
                                <ListItemText primary={file.filename} />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={Boolean(
                                                file.condiviso_partecipanti
                                            )}
                                            onChange={(event) =>
                                                toggleExistingFileSharing(
                                                    file,
                                                    event.target.checked
                                                )
                                            }
                                        />
                                    }
                                    label="Partecipanti"
                                />
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleDownload(file)}
                                        title="Scarica"
                                    >
                                        <Download />
                                    </IconButton>
                                    <IconButton
                                        edge="end"
                                        onClick={() =>
                                            openDeleteDialog(
                                                file.id,
                                                file.filename
                                            )
                                        }
                                        title="Elimina"
                                        color="error"
                                    >
                                        <Delete />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                </div>
            )}

            {isEditMode &&
                existingFiles.length === 0 &&
                newFiles.length === 0 &&
                !loading && (
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ textAlign: 'center', marginTop: '1rem' }}
                    >
                        Nessun file caricato per questo corso
                    </Typography>
                )}

            <Dialog
                open={deleteDialog.open}
                onClose={closeDeleteDialog}
                aria-labelledby="delete-file-dialog-title"
            >
                <DialogTitle id="delete-file-dialog-title">
                    Conferma eliminazione
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Sei sicuro di voler eliminare il file{' '}
                        <strong>"{deleteDialog.filename}"</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} color="primary">
                        Annulla
                    </Button>
                    <Button
                        onClick={handleDeleteExistingFile}
                        color="error"
                        variant="contained"
                        autoFocus
                    >
                        Elimina
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default AddAllegatiInput;
